// /settings/firm - the admin's editor for the firm identity that prints
// on invoices, court forms and client emails. Text fields buffer locally
// and one explicit Save carries only the changed keys.

import { useEffect, useMemo, useState } from "react";
import {
  useFirmConfig,
  useSaveFirmConfig,
  type FirmAddress,
  type FirmConfig,
  type FirmConfigPatch,
} from "../api/attorneyConfig";
import { useCurrentUser } from "../components/auth/CurrentUserContext";
import { isAdminRole } from "../lib/roles";
import { Skeleton, SkeletonGroup } from "../components/chrome/Skeleton";
import { SettingsNav } from "./settings/SettingsNav";

// Mirrors the server's Zod cap so the admin hears "too big" before a
// 400KB base64 round-trip, not after.
const MAX_LOGO_BYTES = 300 * 1024;
const MAX_LOGO_SIDE = 500;

// The browser's own zone list, read when the page renders: an older
// Safari has no supportedValuesOf, and a throw at module level would
// blank the bundle. A zone twelve or more hours ahead of UTC is left out,
// as the server refuses it (a bare day is stored at noon UTC).
function zoneList(): string[] {
  if (typeof Intl.supportedValuesOf !== "function") return [];
  const year = new Date().getUTCFullYear();
  return Intl.supportedValuesOf("timeZone").filter((tz) => {
    const day = new Intl.DateTimeFormat("en-CA", { timeZone: tz });
    for (let month = 0; month < 12; month++) {
      const noon = new Date(Date.UTC(year, month, 15, 12));
      if (day.format(noon) !== noon.toISOString().slice(0, 10)) return false;
    }
    return true;
  });
}

// Mirrors the server's square check the same way: the width and height
// are the first two fields of IHDR, the chunk every PNG opens with.
function pngSize(bytes: Uint8Array): { width: number; height: number } | null {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const png =
    bytes.length >= 24 && view.getUint32(0) === 0x89504e47 && view.getUint32(12) === 0x49484452;
  return png ? { width: view.getUint32(16), height: view.getUint32(20) } : null;
}

function clone(cfg: FirmConfig): FirmConfig {
  return { ...cfg, address: { ...cfg.address } };
}

function diff(base: FirmConfig, draft: FirmConfig): FirmConfigPatch {
  const patch: FirmConfigPatch = {};
  for (const key of Object.keys(draft) as (keyof FirmConfig)[]) {
    if (key === "address") continue;
    if (draft[key] !== base[key]) {
      (patch as Record<string, string>)[key] = draft[key] as string;
    }
  }
  const address: Partial<FirmAddress> = {};
  for (const key of Object.keys(draft.address) as (keyof FirmAddress)[]) {
    if (draft.address[key] !== base.address[key]) address[key] = draft.address[key];
  }
  if (Object.keys(address).length > 0) patch.address = address;
  return patch;
}

function Field({
  label,
  value,
  onChange,
  hint,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  hint?: string;
}) {
  return (
    <label className="settings-field">
      <span className="settings-label">{label}</span>
      <input className="cc-field" value={value} onChange={(e) => onChange(e.target.value)} />
      {hint && <span className="settings-hint">{hint}</span>}
    </label>
  );
}

export function FirmSettings() {
  const user = useCurrentUser();
  const isAdmin = isAdminRole(user?.role);
  const cfgQ = useFirmConfig(isAdmin);
  const save = useSaveFirmConfig();
  const [draft, setDraft] = useState<FirmConfig | null>(null);
  const [logoError, setLogoError] = useState<string | null>(null);
  const zones = useMemo(zoneList, []);

  useEffect(() => {
    if (cfgQ.data && !draft) setDraft(clone(cfgQ.data));
  }, [cfgQ.data, draft]);

  const set = (key: keyof Omit<FirmConfig, "address">, value: string) =>
    setDraft((d) => (d ? { ...d, [key]: value } : d));
  const setAddr = (key: keyof FirmAddress, value: string) =>
    setDraft((d) => (d ? { ...d, address: { ...d.address, [key]: value } } : d));

  const patch = cfgQ.data && draft ? diff(cfgQ.data, draft) : {};
  const dirty = Object.keys(patch).length > 0;

  function onLogoFile(file: File | undefined) {
    setLogoError(null);
    if (!file) return;
    if (file.type !== "image/png") {
      setLogoError("The logo must be a PNG image.");
      return;
    }
    if (file.size > MAX_LOGO_BYTES) {
      setLogoError("The logo must be 300KB or smaller.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const url = String(reader.result);
      // The header is the 24 bytes behind the first 32 base64 characters.
      const start = url.indexOf(",") + 1;
      const head = Uint8Array.from(atob(url.slice(start, start + 32)), (c) => c.charCodeAt(0));
      const size = pngSize(head);
      if (!size || size.width < 1 || size.width !== size.height) {
        setLogoError("The logo must be a square PNG.");
      } else if (size.width > MAX_LOGO_SIDE) {
        setLogoError(`The logo must be ${MAX_LOGO_SIDE} by ${MAX_LOGO_SIDE} pixels or smaller.`);
      } else {
        set("logo", url);
      }
    };
    reader.readAsDataURL(file);
  }

  if (!isAdmin) {
    // A null user is an unconfirmed identity (the /me probe soft-passed),
    // not a known non-admin - the copy must not claim otherwise.
    return (
      <main className="cc-main">
        <div className="cc-body settings-body">
          <SettingsNav />
          <section className="cc-card">
            <h3>Firm settings</h3>
            <p className="settings-sub">
              {user
                ? "Admin access is required to edit the firm profile."
                : "Could not confirm your account. Refresh to retry."}
            </p>
          </section>
        </div>
      </main>
    );
  }

  return (
    <main className="cc-main">
      <div className="cc-body settings-body">
        <SettingsNav />
        <header className="settings-head">
          <div>
            <h2>Firm settings</h2>
            <p className="settings-sub">
              These details print on invoices, court forms and client emails.
            </p>
          </div>
          <div className="settings-save">
            <span className={"settings-status" + (save.isError ? " bad" : "")} role="status">
              {save.isPending
                ? "Saving..."
                : save.isError
                  ? (save.error as Error).message
                  : dirty
                    ? "Unsaved changes"
                    : save.isSuccess
                      ? "Saved"
                      : ""}
            </span>
            <button
              type="button"
              className="settings-save-btn"
              disabled={!dirty || save.isPending || !draft}
              onClick={() => save.mutate(patch)}
            >
              Save changes
            </button>
          </div>
        </header>

        {cfgQ.isLoading && (
          <section className="cc-card">
            <SkeletonGroup>
              <Skeleton width={220} height={20} />
              <Skeleton />
              <Skeleton width={180} />
            </SkeletonGroup>
          </section>
        )}
        {cfgQ.isError && (
          <section className="cc-card">
            <p className="settings-error">Could not load the firm profile. Refresh to retry.</p>
          </section>
        )}

        {draft && (
          <div className="settings-cols">
            <div className="settings-col">
              <section className="cc-card settings-section">
                <h3>Identity</h3>
                <div className="settings-grid">
                  <Field
                    label="Firm name"
                    value={draft.firmName}
                    onChange={(v) => set("firmName", v)}
                  />
                  <Field
                    label="Organization name"
                    value={draft.organizationName}
                    onChange={(v) => set("organizationName", v)}
                    hint="On G-28 and EOIR forms, when it differs from the firm name"
                  />
                  <Field
                    label="Attorney first name"
                    value={draft.firstName}
                    onChange={(v) => set("firstName", v)}
                  />
                  <Field
                    label="Middle name"
                    value={draft.middleName}
                    onChange={(v) => set("middleName", v)}
                  />
                  <Field
                    label="Last name"
                    value={draft.lastName}
                    onChange={(v) => set("lastName", v)}
                  />
                  <Field label="Suffix" value={draft.suffix} onChange={(v) => set("suffix", v)} />
                  <Field
                    label="Display name"
                    value={draft.fullName}
                    onChange={(v) => set("fullName", v)}
                    hint="Shown to clients and on letterheads, e.g. Robert Bond, Esq."
                  />
                  <Field
                    label="Bar number"
                    value={draft.barNumber}
                    onChange={(v) => set("barNumber", v)}
                  />
                  <Field
                    label="Bar state"
                    value={draft.barState}
                    onChange={(v) => set("barState", v)}
                    hint="Prints as the licensing authority on G-28"
                  />
                  <Field label="EOIR ID" value={draft.eoirId} onChange={(v) => set("eoirId", v)} />
                  <Field
                    label="USCIS online account"
                    value={draft.uscisOnlineAcct}
                    onChange={(v) => set("uscisOnlineAcct", v)}
                  />
                </div>
              </section>

              <section className="cc-card settings-section">
                <h3>Mailing address</h3>
                <div className="settings-grid">
                  <Field
                    label="Street"
                    value={draft.address.street}
                    onChange={(v) => setAddr("street", v)}
                  />
                  <Field
                    label="Apt / Suite"
                    value={draft.address.aptSuite}
                    onChange={(v) => setAddr("aptSuite", v)}
                  />
                  <Field
                    label="City"
                    value={draft.address.city}
                    onChange={(v) => setAddr("city", v)}
                  />
                  <Field
                    label="State"
                    value={draft.address.state}
                    onChange={(v) => setAddr("state", v)}
                  />
                  <Field
                    label="ZIP"
                    value={draft.address.zip}
                    onChange={(v) => setAddr("zip", v)}
                  />
                  <Field
                    label="Country"
                    value={draft.address.country}
                    onChange={(v) => setAddr("country", v)}
                  />
                </div>
              </section>
            </div>

            <div className="settings-col">
              <section className="cc-card settings-section">
                <h3>Contact</h3>
                <div className="settings-grid">
                  <Field
                    label="Phone"
                    value={draft.phone}
                    onChange={(v) => set("phone", v)}
                    hint="Printed on invoices and in client emails"
                  />
                  <Field label="Mobile" value={draft.mobile} onChange={(v) => set("mobile", v)} />
                  <Field label="Fax" value={draft.fax} onChange={(v) => set("fax", v)} />
                  <Field label="Email" value={draft.email} onChange={(v) => set("email", v)} />
                  <Field
                    label="Website"
                    value={draft.website}
                    onChange={(v) => set("website", v)}
                  />
                </div>
              </section>

              <section className="cc-card settings-section">
                <h3>Calendar</h3>
                <p className="settings-sub">
                  Deadline windows, billing reports and the intake exports run on this zone. Dates
                  and day counts on screen follow your browser.
                </p>
                <label className="settings-field">
                  <span className="settings-label">Time zone</span>
                  <select
                    className="cc-field"
                    value={draft.timezone}
                    onChange={(e) => set("timezone", e.target.value)}
                  >
                    {/* A stored zone off the list stays selectable. */}
                    {(zones.includes(draft.timezone) ? zones : [draft.timezone, ...zones]).map(
                      (tz) => (
                        <option key={tz} value={tz}>
                          {tz}
                        </option>
                      ),
                    )}
                  </select>
                </label>
              </section>

              <section className="cc-card settings-section">
                <h3>Logo</h3>
                <p className="settings-sub">
                  Stands in the rail beside the attorney and prints on invoice letterheads. A square
                  PNG with a transparent background, up to 500 by 500 pixels and 300KB.
                </p>
                <div className="settings-logo-row">
                  {draft.logo ? (
                    <img className="settings-logo-preview" src={draft.logo} alt="Firm logo" />
                  ) : (
                    <span className="settings-logo-empty">No logo uploaded</span>
                  )}
                  <div className="settings-logo-actions">
                    <label className="settings-upload">
                      Upload PNG
                      <input
                        type="file"
                        accept="image/png"
                        onChange={(e) => {
                          onLogoFile(e.target.files?.[0]);
                          e.target.value = "";
                        }}
                      />
                    </label>
                    {draft.logo && (
                      <button
                        type="button"
                        className="cc-link-btn danger"
                        onClick={() => set("logo", "")}
                      >
                        Remove logo
                      </button>
                    )}
                  </div>
                </div>
                {logoError && <p className="settings-error">{logoError}</p>}
              </section>
            </div>
          </div>
        )}
      </div>
    </main>
  );
}
